import React from "react";
import PropTypes from "prop-types";
import { Tooltip } from "@mui/material";

/**
 * The hover card for Build Your Own tiles — the same dark, arrowed DM Sans
 * tooltip the Beat the Yeti ⓘ uses (#0F172A, 8px radius), with a little
 * structure: an eyebrow, label/value rows, one verdict line, a quiet footnote.
 * Tones are the dark-surface versions of the board's green / red.
 */
const TONES = { good: "#4ADE80", bad: "#F87171", flat: "#94A3B8" };
const FONT = '"DM Sans", system-ui, sans-serif';

const TIP_PROPS = {
  arrow: true,
  placement: "top",
  enterDelay: 120,
  componentsProps: {
    tooltip: {
      sx: {
        fontFamily: FONT,
        backgroundColor: "#0F172A",
        borderRadius: "8px",
        padding: "7px 10px",
        maxWidth: 190,
        boxShadow: "0 6px 18px rgba(15,23,42,0.24)",
      },
    },
    arrow: { sx: { color: "#0F172A" } },
  },
};

const Body = ({ eyebrow, rows, verdict, note }) => (
  <div style={{ fontFamily: FONT, minWidth: 132 }}>
    <div style={{ fontSize: 8.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#94A3B8", marginBottom: 3 }}>
      {eyebrow}
    </div>
    {rows.map((r) => (
      <div key={r.label} style={{ display: "flex", justifyContent: "space-between", gap: 12, fontSize: 11, lineHeight: 1.5 }}>
        <span style={{ color: "#CBD5E1" }}>{r.label}</span>
        <span style={{ color: "#F8FAFC", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{r.value}</span>
      </div>
    ))}
    {verdict && (
      <div style={{
        marginTop: 4, paddingTop: 4, borderTop: "1px solid rgba(148,163,184,0.22)",
        fontSize: 11, fontWeight: 700, color: TONES[verdict.tone] || TONES.flat,
      }}
      >
        {verdict.text}
      </div>
    )}
    {note && <div style={{ marginTop: 4, fontSize: 9.5, lineHeight: 1.35, color: "#94A3B8", fontWeight: 400 }}>{note}</div>}
  </div>
);

Body.propTypes = {
  eyebrow: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })).isRequired,
  verdict: PropTypes.shape({ text: PropTypes.string, tone: PropTypes.string }),
  note: PropTypes.string,
};
Body.defaultProps = { verdict: null, note: "" };

/* `className` / `style` land on the wrapper span, for a tip that has to be
   positioned itself (the goal notch sits on a bar). */
const TileTip = ({ children, className, style, ...body }) => (
  <Tooltip title={<Body {...body} />} {...TIP_PROPS}>
    <span className={className} style={{ display: "inline-flex", cursor: "help", ...style }}>{children}</span>
  </Tooltip>
);

TileTip.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  style: PropTypes.object,
};
TileTip.defaultProps = { className: undefined, style: undefined };

export default TileTip;
